'use client'
import type { FormFieldBlock, Form as FormType } from '@payloadcms/plugin-form-builder/types'

import { useRouter } from 'next/navigation'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import RichText from '@/components/RichText'
import { Button } from '@/components/ui/button'
import type { DefaultTypedEditorState } from '@payloadcms/richtext-lexical'

import { fields } from './fields'
import { AltchaWidget } from './AltchaWidget'
import { getClientSideURL } from '@/utilities/getURL'
import { TypedLocale } from 'payload'

export type FormBlockType = {
  blockName?: string
  blockType?: 'formBlock'
  enableIntro: boolean
  form: FormType
  introContent?: DefaultTypedEditorState
}

export const FormBlockClient: React.FC<
  {
    id?: string
    locale: TypedLocale
  } & FormBlockType
> = (props) => {
  const {
    enableIntro,
    form: formFromProps,
    form: { id: formID, confirmationMessage, confirmationType, redirect, submitButtonLabel } = {},
    introContent,
    locale,
  } = props

  const formMethods = useForm({
    defaultValues: formFromProps.fields,
  })
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
  } = formMethods

  const [isLoading, setIsLoading] = useState(false)
  const [hasSubmitted, setHasSubmitted] = useState<boolean>()
  const [error, setError] = useState<{ message: string; status?: string } | undefined>()
  const router = useRouter()

  const formRef = useRef<HTMLFormElement>(null)
  const altchaPayloadRef = useRef<string | null>(null)
  // Zeitpunkt, an dem das Formular erschien: zu schnelles Absenden gilt als Bot.
  const mountedAtRef = useRef<number>(0)

  type AltchaElement = HTMLElement & { verify?: () => Promise<void>; reset?: () => void }
  const getWidget = () => formRef.current?.querySelector<AltchaElement>('altcha-widget') ?? null

  useEffect(() => {
    mountedAtRef.current = Date.now()
  }, [])

  useEffect(() => {
    const widget = getWidget()
    if (!widget) return

    const onVerified = (e: Event) => {
      altchaPayloadRef.current = (e as CustomEvent<{ payload?: string }>).detail?.payload ?? null
    }
    widget.addEventListener('verified', onVerified)
    return () => widget.removeEventListener('verified', onVerified)
  }, [hasSubmitted])

  const onSubmit = useCallback(
    (data: FormFieldBlock[]) => {
      const submitForm = async () => {
        setError(undefined)
        setIsLoading(true)

        const dataToSend = Object.entries(data).map(([name, value]) => ({
          field: name,
          value,
        }))

        try {
          // Loesung des ALTCHA-Widgets; fehlt sie noch, jetzt berechnen lassen.
          const widget = getWidget()
          if (!altchaPayloadRef.current && widget?.verify) {
            await widget.verify()
            await new Promise<void>((r) => queueMicrotask(r))
          }
          const altcha = altchaPayloadRef.current
          if (!altcha) {
            setIsLoading(false)
            setError({
              message: 'Die Überprüfung ist fehlgeschlagen. Bitte versuchen Sie es erneut.',
            })
            return
          }

          const req = await fetch(`${getClientSideURL()}/api/form-submissions`, {
            body: JSON.stringify({
              form: formID,
              submissionData: dataToSend,
            }),
            headers: {
              'Content-Type': 'application/json',
              'x-altcha': altcha,
              'x-form-mounted-at': String(mountedAtRef.current),
            },
            method: 'POST',
          })

          const res = await req.json()

          // Eine Loesung gilt nur einmal: bei Fehler neu berechnen lassen.
          altchaPayloadRef.current = null

          if (req.status >= 400) {
            setIsLoading(false)
            getWidget()?.reset?.()
            void getWidget()?.verify?.()

            setError({
              message: res.errors?.[0]?.message || 'Internal Server Error',
              status: res.status,
            })

            return
          }

          // Add a 2 second timeout before proceeding after successful submission
          await new Promise((resolve) => setTimeout(resolve, 2000))

          setIsLoading(false)
          setHasSubmitted(true)

          if (confirmationType === 'redirect' && redirect) {
            const { url } = redirect

            const redirectUrl = url

            if (redirectUrl) router.push(redirectUrl)
          }
        } catch (err) {
          console.warn(err)
          setIsLoading(false)
          setError({
            message: 'Something went wrong.',
          })
        }
      }

      void submitForm()
    },
    [router, formID, redirect, confirmationType],
  )

  return (
    <div className="container py-20 lg:max-w-3xl">
      {enableIntro && introContent && !hasSubmitted && (
        <RichText className="mb-8 lg:mb-12" data={introContent} enableGutter={false} />
      )}
      <div className="rounded-[0.8rem] border border-border p-4 lg:p-6">
        <FormProvider {...formMethods}>
          {!isLoading && hasSubmitted && confirmationType === 'message' && (
            <RichText data={confirmationMessage} />
          )}
          {error && (
            <div className="mb-4 text-red-600">{`${error.status || '500'}: ${error.message || ''}`}</div>
          )}
          {!hasSubmitted && (
            <form ref={formRef} id={formID} onSubmit={handleSubmit(onSubmit)}>
              <AltchaWidget />
              <div className="mb-4 flex flex-row flex-wrap justify-between gap-y-2 last:mb-0">
                {formFromProps &&
                  formFromProps.fields &&
                  formFromProps.fields?.map((field, index) => {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const Field: React.FC<any> = fields?.[field.blockType as keyof typeof fields]
                    if (Field) {
                      return (
                        <div
                          className="mb-1 w-full last:mb-0"
                          style={{
                            flexBasis: 'width' in field && field.width ? `${field.width}%` : '100%',
                          }}
                          key={index}
                        >
                          <Field
                            form={formFromProps}
                            {...field}
                            {...formMethods}
                            control={control}
                            errors={errors}
                            register={register}
                            locale={locale}
                          />
                        </div>
                      )
                    }
                    return null
                  })}
              </div>

              <Button form={formID} type="submit" variant="default" disabled={isLoading}>
                {isLoading && (
                  <svg
                    className="-ml-1 mr-3 h-4 w-4 animate-spin"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                )}
                {isLoading ? 'Wird gesendet...' : submitButtonLabel}
              </Button>
            </form>
          )}
        </FormProvider>
      </div>
    </div>
  )
}
